<template>
  <main class="main skin">
    <PagesItem
        v-if="item"
        :item="item as ItemPageInfo"
        :loading="loading"
    />
  </main>
</template>

<script lang="ts" setup>
import {getItemGeneral} from "~/utils/api";
import {EventBus} from '~/composables/bus';
import type {ItemPageInfo} from "~/graphql/generated/schema-types";
import {useStore} from "~/store";
import {getImageFromWaxpeer, getItemLinkWithExterior} from "#imports";
import {useDefineProduct} from "~/composables/define-product";
import {useAlternateLinks} from "~/composables/alternate-links";
import {getItemLinkWithoutExtAndStatrack} from "~/utils/name-utils";

const store = useStore()
const user = computed(() => store.user)
const route = useRoute()
const item = ref<ItemPageInfo | null>(null)
const {locale, t} = useI18n()
const config = useRuntimeConfig()
const siteHost = config.public.siteHost
const {defineUseProduct} = useDefineProduct()
const loading = ref(false)

async function getItemFetch() {
  const itemName = route.params.name
  const exterior = route.params.exterior || ''
  const fullName = `${itemName}${exterior ? `-${exterior}` : ''}`
  try {
    const {data, errors} = await getItemGeneral({name: fullName})

    if (errors?.length) {
      console.log(`data: ${data}, Item page exterior fetch error: ${JSON.stringify(errors)}, method: getItem({name: fullName}), fullName = ${fullName}`)

      throw new Error(errors[0].message)
    }

    // const {data: data2, errors: errors2} = await getItemRating(data.itemPageInfo.id)
    //
    // if (errors2?.length) {
    //   EventBus.$emit('notify', {
    //     success: false,
    //     msg: errors2[0].message || 'An error occurred'
    //   })
    //   return {...data.itemPageInfo}
    // }

    return {
      ...data.itemPageInfo,
      // ...data2.getItemRating
    }
  } catch (e: any) {
    console.log('Item page exterior fetch error: ', e.message)
    EventBus.$emit('notify', {
      success: false,
      msg: e.message || 'An error occurred'
    })
    throw new Error(e.message || 'An error occurred')
  }
}

const {error, data} = await useAsyncData('getItem-exterior', getItemFetch, {watch: [route]});

if (error.value) {
  if (process.client) {
    window.location.reload();
  }
  console.log('Item exterior page fetch error: ' + error.value?.message)
  throw createError({
    status: 404,
    statusText: 'Item exterior page fetch error: ' + error.value?.message
  });
} else {
  item.value = data.value
  if (data.value?.category === 'Collections') {
    await navigateTo('/collections' + '/' + route.params.name, {redirectCode: 301})
  }
}

const itemTitle = computed(() => {
  let phase = ''
  if (item.value?.phase) {
    phase = ` ${t('phases.' + item.value?.phase)}`
  }
  const itemName = (item.value?.locals?.localed_name ? item.value?.locals?.localed_name + (phase || '') : '') ||
      item.value?.market_hash_name

  return t('heads.item.title', {name: itemName})
})

const itemDescription = computed(() => {
  if (item.value?.rarity_color === '#eb4b4b') {
    return t('heads.item.desc_covert', {name: item.value?.locals?.localed_name || item.value?.market_hash_name || ''})
  }
  if (item.value?.rarity_color === '#d32ce6') {
    return t('heads.item.desc_classified', {name: item.value?.locals?.localed_name || item.value?.market_hash_name || ''})
  }
  if (item.value?.rarity_color === '#b0c3d9') {
    return t('heads.item.desc_consumer', {name: item.value?.locals?.localed_name || item.value?.market_hash_name || ''})
  }
  if (item.value?.rarity_color === '#5e98d9') {
    return t('heads.item.desc_industrial', {name: item.value?.locals?.localed_name || item.value?.market_hash_name || ''})
  }
  return t('heads.item.desc_default', {name: item.value?.locals?.localed_name || item.value?.market_hash_name || ''})
  // return item.value?.locals?.description || t('heads.item.description', {name: item.value?.market_hash_name || ''})
})

const itemIcon = computed(() => {
  return getImageFromWaxpeer(item.value?.market_hash_name || '')
})

const {canonical} = useAlternateLinks(getItemLinkWithoutExtAndStatrack({
  name: item.value?.market_hash_name,
  usedLocalePath: false,
  slug: item.value?.link
}))

useHead({
  title: itemTitle.value,
  titleTemplate: '%s', // Переопределяем titleTemplate для конкретной страницы
  meta: [
    {
      name: 'keywords',
      content: 'steam, counter strike global offensive, csgo, cs2, skins, items'
    },
  ],
  link: () => [
    // ...alternateLinks.value,
    {
      rel: 'canonical',
      href: canonical.value
    },
    {
      href: getImageFromWaxpeer(item.value?.market_hash_name || '', '500:500'),
      rel: 'preload',
      as: 'image',
      fetchpriority: 'high'
    }
  ]
})

useSeoMeta({
  title: () => itemTitle.value,
  ogTitle: () => itemTitle.value,
  description: () => itemDescription.value,
  ogDescription: () => itemDescription.value,
  ogImage: () => itemIcon.value,
  twitterCard: 'summary_large_image',
  twitterTitle: () => itemTitle.value,
  twitterDescription: () => itemDescription.value,
  twitterImage: () => itemIcon.value,
  ogSiteName: 'CSMarketCap',
  ogUrl: () => canonical.value,
  ogType: 'website',
  ogLocale: () => locale.value
})

useSchemaOrg([
  await defineUseProduct(item.value as ItemPageInfo & {
    rating: number,
    votes: number
  }),

  defineBreadcrumb({
    itemListElement: [
      {name: () => t('main'), item: '/'},
      {
        name: getWeaponOnly(item.value?.market_hash_name || ''),
        item: getWeaponLink(getWeaponOnly(item.value?.market_hash_name || ''))
      },
      {
        name: itemTitle.value,
        item: getItemLinkWithExterior({
          name: item.value?.market_hash_name || '',
          slug: item.value?.link
        })
      },
    ],
  })
])

</script>

<style scoped>

</style>
